import type { FC } from 'react';
import { useCopyCode } from '@/hooks/useCopyCode.hook';
import type { Bookmaker } from '@/types/bookmaker';
import { getBookmakerDetailsUrl } from '@/utils/bookmaker';
import { cn } from '@/utils/cn';
import { Button } from '../button';
import { LogoPiece2Icon } from '../icons/LogoPiece2Icon';
import { PagesIcon } from '../icons/PagesIcon';
import MarkdownContent from '../MarkdownContent';
import { BookmakerLogoImage } from './components/BookmakerLogoImage';
import { PaymentIcons } from './components/PaymentIcons';

interface FeaturedBookmakerCardProps {
  data: Bookmaker;
}

export const FeaturedBookmakerCard: FC<FeaturedBookmakerCardProps> = ({
  data,
}) => {
  const {
    BookmakerUrl,
    Code,
    OurReviewList,
    BonusOffer,
    BookmakerLogo,
    Slug,
    BookmakerName,
    PaymentType,
  } = data.attributes;
  const reviewList = Object.values(OurReviewList ?? {});

  const imageUrl = BookmakerLogo?.data?.attributes?.url;
  const bookmakerDetailsUrl = getBookmakerDetailsUrl(Slug);

  return (
    <div className="grid gap-3 rounded-lg bg-component-bg px-3 py-4 hover:bg-light-blue-o15 md:grid-cols-[1fr_205px_auto]">
      <div className="flex flex-col gap-1">
        <BookmakerLogoImage
          alt={`${BookmakerName} logo`}
          imageUrl={imageUrl}
          url={BookmakerUrl}
        />
        <CopyCode className="hidden md:flex" code={Code} />
      </div>
      <div className="rounded-lg bg-custom-gradient-2 p-2">
        <div className="flex flex-col gap-1">
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <BookmakerOffer
                className="flex-1"
                name={BookmakerName}
                offerMarkdown={BonusOffer}
              />
              <CopyCode className="flex-1 md:hidden" code={Code} />
            </div>

            <div className="flex flex-1 flex-col justify-center gap-1 rounded-lg bg-card-bg px-4 py-2">
              <p className="inline-block font-normal text-[10px] text-gray leading-[12px]">
                Accepted payments
              </p>
              <PaymentIcons paymentType={PaymentType} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <p className="font-semibold text-gray text-sm leading-4">Our review</p>
        <ul className="ml-5 list-disc">
          {reviewList.slice(0, 5).map((reviewLine) => {
            return (
              <li
                className="font-normal text-white text-xs leading-5"
                key={reviewLine}
              >
                {reviewLine}
              </li>
            );
          })}
        </ul>
        <div className="grid grid-cols-2 items-center gap-3">
          <Button
            aria-label={`View review for ${BookmakerName}`}
            as="link"
            href={bookmakerDetailsUrl}
            size="lg"
            variant="ghost"
          >
            View review
          </Button>
          <Button
            aria-label={`Bet now with ${BookmakerName} (opens in new tab)`}
            as="link"
            href={BookmakerUrl}
            size="lg"
            target="_blank"
            variant="outline"
          >
            Bet Now
          </Button>
        </div>
      </div>
    </div>
  );
};

function BookmakerOffer({
  name,
  offerMarkdown,
  className,
}: {
  name: string;
  offerMarkdown: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'space-between relative flex h-[100px] flex-col overflow-hidden rounded-lg bg-light-blue-o15 p-2',
        className
      )}
    >
      <p className="font-normal text-[10px] leading-4">{name} offer</p>
      <article className="line-clamp-3">
        <MarkdownContent className="bookmaker-offer-markdown text-sm">
          {offerMarkdown}
        </MarkdownContent>
      </article>
      <div className="absolute right-0 bottom-0 bg-contain bg-no-repeat">
        <LogoPiece2Icon
          className="h-auto w-10"
          desc="GoCore logo piece decoration"
          title="Logo Decoration"
        />
      </div>
    </div>
  );
}

function CopyCode({ code, className }: { code: string; className?: string }) {
  const { copyCode } = useCopyCode(code);

  return (
    <div
      className={cn(
        'flex flex-row items-center justify-center gap-2 rounded-lg bg-light-gray px-3 py-4',
        className
      )}
    >
      <PagesIcon
        className="h-7 w-7 text-blue-gray"
        desc="Copy code icon"
        title="Copy Code"
      />
      <button
        className="cursor-pointer font-normal text-gray text-xs leading-4"
        onClick={copyCode}
        type="button"
      >
        Copy code and get a welcome gift
      </button>
    </div>
  );
}
